// ----------------------------------------------------------------------------
// BidDrawer
// ----------------------------------------------------------------------------
// Right-side slide-out detail panel (~35% width on desktop). Renders metadata
// for the currently selected bid and exposes a working client-side download.
//
// In production: replace `BID_DETAILS[bid.id]` lookups with a fetch call to a
// `/api/bids/:id` endpoint, and replace the Blob download with an S3 pre-signed
// URL fetched from the same record.
// ----------------------------------------------------------------------------

import React, { useState } from "react";
import {
  X,
  MapPin,
  Building2,
  ShieldCheck,
  Download,
  FileText,
  Tag,
  Hash,
  CalendarClock,
  Bookmark,
  BookmarkCheck,
} from "lucide-react";
import { toast } from "sonner";
import { BID_DETAILS } from "../data/mockData";
import { DRAWER } from "../constants/testIds";
import { useSavedContracts } from "../context/SavedContractsContext";
import { AiProposalSection } from "./AiProposalSection";

// Build the placeholder text file body and trigger a client-side download.
// If a user-generated AI proposal is supplied it is prepended to the file so
// the downloaded artifact reflects the buyer's tailored submission.
const triggerDownload = (bid, proposal) => {
  const detail = BID_DETAILS[bid.id] || {};
  const proposalBlock = proposal
    ? `================================================================
GENERATED AI BID PROPOSAL
================================================================
${proposal}

`
    : "";
  const body = `SPACE COAST PROCUREMENT PORTAL
================================================================
BID PACKAGE EXPORT
================================================================

${proposalBlock}Tracking ID   : ${bid.id}
Project Title : ${bid.title}
Agency        : ${bid.agency}
Platform      : ${bid.platform}
Location      : ${bid.location}
Closing Date  : ${bid.closing}
Solicitation  : ${bid.type}
Value Tier    : ${bid.value}

----------------------------------------------------------------
TECHNICAL SCOPE OF WORK
----------------------------------------------------------------
${detail.scope || "Refer to attached solicitation document."}

----------------------------------------------------------------
SITE LOCATIONS
----------------------------------------------------------------
${(detail.sites || []).map((s, i) => `  ${i + 1}. ${s}`).join("\n")}

----------------------------------------------------------------
BOND & INSURANCE REQUIREMENTS
----------------------------------------------------------------
${detail.bond || "Standard county requirements apply."}

================================================================
[SaaS Production Note: In a live environment, this button connects to
an AWS S3 bucket link stored in the database entry.]
================================================================
`;
  const blob = new Blob([body], { type: "text/plain;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `bid_package_${bid.id}.txt`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
  toast.success("Bid package downloaded", {
    description: `bid_package_${bid.id}.txt`,
  });
};

export const BidDrawer = ({ bid, open, onClose }) => {
  const { isSaved, toggleBid } = useSavedContracts();
  // Generated proposal text (per drawer-open session). Stays in sync with the
  // AiProposalSection child and is piped into the download payload.
  const [proposal, setProposal] = useState("");
  if (!open || !bid) return null;
  const detail = BID_DETAILS[bid.id] || {};
  const saved = isSaved(bid.id);

  const onToggleSave = () => {
    const result = toggleBid(bid);
    if (result === "added") {
      toast.success("Contract saved", { description: bid.title });
    } else {
      toast.message("Removed from saved contracts", { description: bid.title });
    }
  };

  return (
    <div className="fixed inset-0 z-50">
      {/* Backdrop */}
      <button
        type="button"
        aria-label="Close drawer"
        data-testid={DRAWER.backdrop}
        onClick={onClose}
        className="absolute inset-0 bg-slate-900/35 backdrop-blur-[2px] animate-fade-in"
      />

      {/* Panel */}
      <aside
        data-testid={DRAWER.root}
        className="absolute top-0 right-0 h-full w-full sm:w-[40%] sm:min-w-[440px] max-w-[640px] bg-white shadow-2xl border-l border-slate-200 flex flex-col animate-drawer-in"
      >
        {/* Header */}
        <div className="px-6 pt-6 pb-5 border-b border-slate-100">
          <div className="flex items-start justify-between gap-4">
            <div className="space-y-2 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[11px] font-medium bg-emerald-100 text-emerald-800 border border-emerald-200">
                  <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                  Active
                </span>
                <span className="text-[11px] uppercase tracking-[0.15em] text-slate-500 font-semibold">
                  {bid.type}
                </span>
              </div>
              <h2 className="font-display text-xl sm:text-2xl font-semibold text-slate-900 tracking-tight leading-snug">
                {bid.title}
              </h2>
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 text-xs text-slate-500">
                <span className="inline-flex items-center gap-1">
                  <Hash size={12} strokeWidth={1.7} />
                  <code className="font-mono text-slate-700">{bid.id}</code>
                </span>
                <span className="inline-flex items-center gap-1">
                  <Building2 size={12} strokeWidth={1.7} /> {bid.agency}
                </span>
                <span className="inline-flex items-center gap-1">
                  <Tag size={12} strokeWidth={1.7} /> {bid.platform}
                </span>
              </div>
            </div>
            <button
              type="button"
              data-testid={DRAWER.close}
              onClick={onClose}
              aria-label="Close"
              className="h-9 w-9 rounded-md text-slate-500 hover:bg-slate-100 hover:text-slate-900 flex items-center justify-center transition-colors"
            >
              <X size={18} strokeWidth={1.7} />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto thin-scroll px-6 py-6 space-y-7">
          {/* Quick stats row */}
          <div className="grid grid-cols-2 gap-3">
            <div className="rounded-md border border-slate-200 bg-slate-50/60 px-3.5 py-3">
              <p className="text-[10px] uppercase tracking-[0.15em] text-slate-500 font-semibold">
                Closing Date
              </p>
              <p className="mt-1 text-sm font-medium text-slate-900 flex items-center gap-1.5">
                <CalendarClock size={14} strokeWidth={1.7} className="text-slate-500" />
                {new Date(bid.closing + "T00:00:00").toLocaleDateString("en-US", {
                  year: "numeric",
                  month: "long",
                  day: "numeric",
                })}
              </p>
            </div>
            <div className="rounded-md border border-slate-200 bg-slate-50/60 px-3.5 py-3">
              <p className="text-[10px] uppercase tracking-[0.15em] text-slate-500 font-semibold">
                Value Tier
              </p>
              <p className="mt-1 text-sm font-medium text-slate-900">{bid.value}</p>
            </div>
          </div>

          {/* Scope */}
          <section>
            <div className="flex items-center gap-2 mb-2.5">
              <FileText size={15} strokeWidth={1.7} className="text-sky-600" />
              <h3 className="text-sm font-semibold text-slate-900 uppercase tracking-[0.1em]">
                Technical Scope of Work
              </h3>
            </div>
            <p className="text-sm text-slate-600 leading-relaxed">{detail.scope}</p>
          </section>

          {/* Site locations */}
          <section>
            <div className="flex items-center gap-2 mb-2.5">
              <MapPin size={15} strokeWidth={1.7} className="text-sky-600" />
              <h3 className="text-sm font-semibold text-slate-900 uppercase tracking-[0.1em]">
                Site Locations
              </h3>
            </div>
            <ul className="space-y-1.5">
              {(detail.sites || []).map((site) => (
                <li
                  key={site}
                  className="text-sm text-slate-700 flex items-start gap-2"
                >
                  <span className="mt-1.5 h-1 w-1 rounded-full bg-sky-500 flex-shrink-0" />
                  {site}
                </li>
              ))}
            </ul>
          </section>

          {/* Bond notice */}
          <section className="rounded-md border border-amber-200 bg-amber-50/70 p-4">
            <div className="flex items-start gap-2.5">
              <ShieldCheck
                size={17}
                strokeWidth={1.7}
                className="text-amber-700 mt-0.5"
              />
              <div>
                <h3 className="text-sm font-semibold text-amber-900 mb-1">
                  Bond & Insurance Requirements
                </h3>
                <p className="text-sm text-amber-900/85 leading-relaxed">
                  {detail.bond}
                </p>
              </div>
            </div>
          </section>

          {/* AI Bid Assistant — sits inside the scrollable body so the entire
              drawer scrolls naturally; visually separated by its own border. */}
          <div className="-mx-6 -mb-6">
            <AiProposalSection
              bid={bid}
              proposal={proposal}
              onProposalChange={setProposal}
            />
          </div>
        </div>

        {/* Footer / CTA */}
        <div className="px-6 py-4 border-t border-slate-100 bg-slate-50/60 space-y-2.5">
          <div className="flex gap-2.5">
            <button
              type="button"
              data-testid={DRAWER.save}
              onClick={onToggleSave}
              aria-pressed={saved}
              className={`inline-flex items-center justify-center gap-2 h-11 px-4 rounded-md font-semibold text-sm border transition-colors flex-shrink-0 ${
                saved
                  ? "bg-sky-50 border-sky-200 text-sky-700 hover:bg-sky-100"
                  : "bg-white border-slate-200 text-slate-700 hover:bg-slate-100"
              }`}
            >
              {saved ? (
                <>
                  <BookmarkCheck
                    size={16}
                    strokeWidth={1.9}
                    className="text-sky-600 fill-sky-200"
                  />
                  Saved
                </>
              ) : (
                <>
                  <Bookmark size={16} strokeWidth={1.9} />
                  Save Contract
                </>
              )}
            </button>
            <button
              type="button"
              data-testid={DRAWER.download}
              onClick={() => triggerDownload(bid, proposal)}
              className="flex-1 inline-flex items-center justify-center gap-2 h-11 px-5 rounded-md bg-[#0c63e7] hover:bg-[#0a4eb8] text-white font-semibold text-sm shadow-sm transition-colors"
            >
              <Download size={16} strokeWidth={1.9} />
              Download Full Bid Package (ZIP)
            </button>
          </div>
          <p className="text-[11px] text-slate-500 text-center">
            {proposal
              ? "Your generated AI proposal is bundled inside the download."
              : "Includes scope, plans, addenda & required attestation forms."}
          </p>
        </div>
      </aside>
    </div>
  );
};
